import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { ColumnDef } from '@tanstack/react-table';
import {
  User,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Eye,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { TopBanner } from '@/components/TopBanner';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useOperators,
  useCreateOperator,
  useUpdateOperator,
  useDeleteOperator,
} from '@/hooks/useOperators';
import type { Operator } from '@/api/operators.api';

export const Route = createFileRoute('/_authenticated/usuarios')({
  component: UsuariosPage,
});

const ROLE_LABELS: Record<string, string> = {
  master: 'Master',
  admin: 'Admin',
  operator: 'Operador',
  viewer: 'Viewer',
};

const ROLE_VARIANTS: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  master: 'destructive',
  admin: 'default',
  operator: 'secondary',
  viewer: 'outline',
};

const ROLE_ICONS: Record<string, React.ReactNode> = {
  master: <ShieldAlert className="h-4 w-4" />,
  admin: <ShieldCheck className="h-4 w-4" />,
  operator: <Shield className="h-4 w-4" />,
  viewer: <Eye className="h-4 w-4" />,
};

const formSchema = z.object({
  username: z.string().min(2, 'Mínimo 2 caracteres').max(60),
  password: z.string().min(4, 'Mínimo 4 caracteres').max(100),
  displayName: z.string().min(1, 'Requerido').max(120),
  role: z.enum(['master', 'admin', 'operator', 'viewer']),
});

type FormValues = z.infer<typeof formSchema>;

function UsuariosPage() {
  const { data: operators = [], isLoading } = useOperators();
  const createMutation = useCreateOperator();
  const updateMutation = useUpdateOperator();
  const deleteMutation = useDeleteOperator();

  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<Operator | null>(null);
  const [deleting, setDeleting] = useState<Operator | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: '',
      password: '',
      displayName: '',
      role: 'operator',
    },
  });

  const onSubmit = async (values: FormValues) => {
    if (editing) {
      await updateMutation.mutateAsync({
        id: editing.id,
        body: {
          displayName: values.displayName,
          role: values.role,
        },
      });
    } else {
      await createMutation.mutateAsync(values);
    }
    setIsOpen(false);
    setEditing(null);
    form.reset();
  };

  const handleEdit = (op: Operator) => {
    setEditing(op);
    form.reset({
      username: op.username,
      password: '',
      displayName: op.displayName,
      role: op.role,
    });
    setIsOpen(true);
  };

  const handleDelete = async () => {
    if (!deleting) return;
    await deleteMutation.mutateAsync(deleting.id);
    setDeleting(null);
  };

  const columns = useMemo<ColumnDef<Operator>[]>(
    () => [
      {
        accessorKey: 'displayName',
        header: 'Nombre',
        cell: ({ row }) => (
          <div className="font-medium">{row.original.displayName}</div>
        ),
      },
      {
        accessorKey: 'username',
        header: 'Usuario',
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">{row.original.username}</span>
        ),
      },
      {
        accessorKey: 'role',
        header: 'Rol',
        cell: ({ row }) => (
          <Badge variant={ROLE_VARIANTS[row.original.role] || 'outline'} className="gap-1">
            {ROLE_ICONS[row.original.role]}
            {ROLE_LABELS[row.original.role] || row.original.role}
          </Badge>
        ),
      },
      {
        accessorKey: 'isActive',
        header: 'Estado',
        cell: ({ row }) => (
          <Badge variant={row.original.isActive ? 'default' : 'secondary'}>
            {row.original.isActive ? 'Activo' : 'Inactivo'}
          </Badge>
        ),
      },
      {
        accessorKey: 'lastLoginAt',
        header: 'Último login',
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {row.original.lastLoginAt
              ? new Date(row.original.lastLoginAt).toLocaleString('es-AR')
              : 'Nunca'}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Button size="icon" variant="ghost" onClick={() => handleEdit(row.original)}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="text-red-500"
              onClick={() => setDeleting(row.original)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <>
      <TopBanner
        title="Usuarios"
        subtitle="Operadores del panel"
        action={
          <Button
            onClick={() => {
              setEditing(null);
              form.reset({ username: '', password: '', displayName: '', role: 'operator' });
              setIsOpen(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Nuevo operador
          </Button>
        }
      />
      <div className="flex-1 overflow-auto p-4 sm:p-6">
        <DataTable
          columns={columns}
          data={operators}
          isLoading={isLoading}
          searchPlaceholder="Buscar operador..."
          searchColumn="displayName"
        />
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar operador' : 'Nuevo operador'}</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="username"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Usuario</FormLabel>
                    <FormControl>
                      <Input {...field} disabled={!!editing} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {!editing && (
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contraseña</FormLabel>
                      <FormControl>
                        <Input type="password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              <FormField
                control={form.control}
                name="displayName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre visible</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Rol</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="master">Master</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="operator">Operador</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                  {(createMutation.isPending || updateMutation.isPending) && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Guardar
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleting} onOpenChange={() => setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Eliminar operador?</DialogTitle>
            <DialogDescription>
              {deleting && (
                <>
                  Se eliminará <strong>{deleting.displayName}</strong> ({deleting.username}).
                  Esta acción no se puede deshacer.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
